import type { IQBLinkageHandler } from '../types';
import { patchBoth } from './shared';

type TAxisConfig = {
  metric?: string;
  metrics?: Array<{ metric: string; tableCode?: string }>;
};

function filterAxis(
  axis: TAxisConfig | undefined,
  removedNames: Set<string>,
): TAxisConfig | undefined {
  if (!axis) {
    return undefined;
  }
  const next = { ...axis };
  let changed = false;
  if (typeof next.metric === 'string' && removedNames.has(next.metric)) {
    delete next.metric;
    changed = true;
  }
  if (Array.isArray(next.metrics)) {
    const filtered = next.metrics.filter(m => !removedNames.has(m.metric));
    if (filtered.length !== next.metrics.length) {
      next.metrics = filtered;
      changed = true;
    }
  }
  return changed ? next : undefined;
}

function axisHasBinding(axis: unknown): boolean {
  if (!axis || typeof axis !== 'object') {
    return false;
  }
  const row = axis as TAxisConfig;
  if (typeof row.metric === 'string' && row.metric) {
    return true;
  }
  return Array.isArray(row.metrics) && row.metrics.length > 0;
}

/** 用户曾配置过双 Y 轴（非仅系统默认 dualYAxisEnabled: false） */
function hasDualYAxisUserConfig(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): boolean {
  if (draft.dualYAxisEnabled === true || base.dualYAxisEnabled === true) {
    return true;
  }
  return (
    axisHasBinding(draft.primaryYAxis ?? base.primaryYAxis) ||
    axisHasBinding(draft.secondYAxis ?? base.secondYAxis)
  );
}

export const dualYAxisHandler: IQBLinkageHandler = {
  id: 'dualYAxis',
  phase: 'prune',
  keys: ['primaryYAxis', 'secondYAxis', 'dualYAxisEnabled'],
  subscribe: ['metricRemoved', 'metricCountChanged'],
  apply(changeSet, ctx) {
    if (!hasDualYAxisUserConfig(ctx.base, ctx.draft)) {
      return;
    }

    const removedNames = new Set(
      changeSet.removedMetrics.map(m => m.column_name),
    );

    if (changeSet.metricCount < 2) {
      return patchBoth(
        {
          dualYAxisEnabled: false,
          primaryYAxis: undefined,
          secondYAxis: undefined,
        },
        {
          dualYAxisEnabled: false,
          primaryYAxis: undefined,
          secondYAxis: undefined,
        },
      );
    }

    const basePatch: Record<string, unknown> = {};
    const draftPatch: Record<string, unknown> = {};
    const primaryBase = filterAxis(
      ctx.base.primaryYAxis as TAxisConfig | undefined,
      removedNames,
    );
    const primaryDraft = filterAxis(
      ctx.draft.primaryYAxis as TAxisConfig | undefined,
      removedNames,
    );
    const secondBase = filterAxis(
      ctx.base.secondYAxis as TAxisConfig | undefined,
      removedNames,
    );
    const secondDraft = filterAxis(
      ctx.draft.secondYAxis as TAxisConfig | undefined,
      removedNames,
    );
    if (primaryBase) {
      basePatch.primaryYAxis = primaryBase;
    }
    if (primaryDraft) {
      draftPatch.primaryYAxis = primaryDraft;
    }
    if (secondBase) {
      basePatch.secondYAxis = secondBase;
    }
    if (secondDraft) {
      draftPatch.secondYAxis = secondDraft;
    }

    if (!Object.keys(basePatch).length && !Object.keys(draftPatch).length) {
      return;
    }

    return patchBoth(
      Object.keys(basePatch).length ? basePatch : undefined,
      Object.keys(draftPatch).length ? draftPatch : undefined,
    );
  },
};
